import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import SafeHtml from '../../components/SafeHtml';
import {
  Bookmark,
  Calendar,
  CheckCircle2,
  ExternalLink,
  FileText,
  Folder,
  GripVertical,
  Image as ImageIcon,
  Info,
  Link as LinkIcon,
  Paintbrush,
  Palette,
  Pencil,
  Star,
  Upload,
  Video,
  Wand2,
  X
} from 'lucide-react';
import {
  buildImageSourceMeta,
  buildMediaNote,
  buildVideoSourceMeta,
  formatInText
} from '../../lib/apaSlideFormatter.js';
import ApaSegments from '../../components/ApaSegments';
import { RichTextEditor } from '../../components/LazyEditors';
import { getDisplayCaption } from '../../utils/imageCaption';


/**
 * Calculates contrasting text color based on background brightness
 * @param {string} hexColor - Background color in hex format
 * @returns {string} - Either dark or light text color
 */
function getContrastingTextColor(hexColor) {
  if (!hexColor || hexColor === 'transparent') return '#1f2937';

  // Remove # if present
  const hex = hexColor.replace('#', '');

  // Convert to RGB
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Calculate brightness (weighted RGB)
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;

  // Return dark text for light backgrounds, light text for dark backgrounds
  return brightness > 150 ? '#1f2937' : '#ffffff';
}

function getBadgeStyles(badge = {}, fallbackColor = '#2563eb') {
  const baseColor = fallbackColor;
  const gradientDirection = badge.gradientDirection || 'to right';

  if (!badge.enabled) {
    return { backgroundColor: baseColor, color: '#ffffff' };
  }

  switch (badge.type) {
    case 'solid':
      return { backgroundColor: badge.color || baseColor, color: '#ffffff' };
    case 'gradient':
      return {
        backgroundColor: badge.color || baseColor,
        backgroundImage: `linear-gradient(${gradientDirection}, ${badge.color || baseColor}, ${badge.color2 || badge.color || baseColor})`,
        color: '#ffffff'
      };
    case 'theme':
    default:
      return { backgroundColor: baseColor, color: '#ffffff' };
  }
}

/**
 * Renders a single card with optional checklist items or rich content
 */
function CardItem({
  card,
  columnIndex,
  cardIndex,
  isEditMode,
  isActive,
  onActivate,
  onDeactivate,
  onCardUpdate,
  onDragStart,
  onDragEnd,
  onRequestImage,
  onRequestVideo,
  isDragging = false,
  fullHeight = false
}) {
  const backgroundColor = card.backgroundColor || 'transparent';
  const borderColor = card.borderColor || '#94a3b8';
  const showBorder = card.showBorder !== false; // Default to true
  const textColor = backgroundColor !== 'transparent'
    ? getContrastingTextColor(backgroundColor)
    : '#1f2937';

  // Support custom title color
  const titleColor = card.titleColor || textColor;

  const categoryLabel = card.categoryLabel;
  const instructionalText = card.instructionalText;
  const numberedList = card.numberedList || [];
  const numberedListStyle = (card.numberedListStyle || 'numbered').toLowerCase();
  const useCheckmarks = numberedListStyle.startsWith('check');
  const titleAlignment = card.titleAlignment || 'center';
  const iconName = card.iconName;

  // Support custom checkmark color
  const checkmarkColor = card.checkmarkColor || textColor;

  const listStyle = card.listStyle || 'checkbox';
  const badgeConfig = { enabled: false, type: 'theme', gradientDirection: 'to right', ...(card.badge || {}) };
  const badgeStyles = getBadgeStyles(badgeConfig, '#2563eb');

  // Callout box configuration
  const callout = card.callout || { enabled: false, title: '', description: '' };

  const titleJustifyMap = {
    left: 'flex-start',
    center: 'center',
    right: 'flex-end',
    justify: 'space-between'
  };

  const titleTextAlign = titleAlignment === 'justify' ? 'justify' : titleAlignment;

  const renderIcon = (name) => {
    const commonProps = { size: 20, strokeWidth: 2.2 };
    switch (name) {
      case 'link':
        return <LinkIcon {...commonProps} />;
      case 'external-link':
        return <ExternalLink {...commonProps} />;
      case 'info':
        return <Info {...commonProps} />;
      case 'star':
        return <Star {...commonProps} />;
      case 'check':
        return <CheckCircle2 {...commonProps} />;
      case 'bookmark':
        return <Bookmark {...commonProps} />;
      case 'file':
        return <FileText {...commonProps} />;
      case 'folder':
        return <Folder {...commonProps} />;
      case 'calendar':
        return <Calendar {...commonProps} />;
      case 'pencil':
        return <Pencil {...commonProps} />;
      case 'brush':
        return <Paintbrush {...commonProps} />;
      case 'palette':
        return <Palette {...commonProps} />;
      default:
        return null;
    }
  };

  const cardStyle = {
    backgroundColor,
    ...(showBorder && {
      borderColor,
      borderWidth: '2px',
      borderStyle: 'solid'
    }),
    color: textColor
  };

  const imageData = card.image || null;
  const videoData = card.video || null;
  const imageCitation = imageData
    ? (() => {
        const includeCitation = imageData.imageIncludeCitation !== false;
        const sourceMeta = buildImageSourceMeta(imageData, {
          sourceType: imageData.imageSourceType || 'image',
          page: imageData.imagePage || '',
          license: imageData.imageLicense || '',
          isAdapted: Boolean(imageData.imageIsAdapted),
          section: imageData.imageTableNumber || ''
        });
        if (!includeCitation) return null;
        return buildMediaNote(sourceMeta);
      })()
    : null;
  const videoMeta = videoData ? buildVideoSourceMeta(videoData) : null;
  const videoCitation = videoMeta ? formatInText(videoMeta) : '';
  const videoUrl = videoMeta ? (videoMeta.url || '').trim() : '';
  const hasVideoCitation = videoData?.videoIncludeCitation !== false && Boolean(videoCitation || videoUrl);

  const placeholderContent = card.content || '';
  const shouldHidePlaceholderContent =
    typeof placeholderContent === 'string' && /Click to (add|edit) title/i.test(placeholderContent);
  const displayContent = shouldHidePlaceholderContent ? '' : placeholderContent;

  const showImagePlaceholder = card.imagePlaceholder && !imageData?.src;
  const showVideoPlaceholder = card.videoPlaceholder && !videoData?.src;

  const headerAlignmentStyle = {
    justifyContent: titleJustifyMap[titleAlignment] || 'center',
    textAlign: titleTextAlign
  };

  const headerStackAlignmentStyle = {
    display: 'flex',
    flexDirection: 'column',
    textAlign: titleTextAlign,
    alignItems:
      titleAlignment === 'left'
        ? 'flex-start'
        : titleAlignment === 'right'
          ? 'flex-end'
          : titleAlignment === 'justify'
            ? 'stretch'
            : 'center'
  };

  const renderCalloutBox = () => {
    if (!callout.enabled || (!callout.title && !callout.description)) {
      return null;
    }

    return (
      <div
        className="mb-4 p-4 rounded-lg"
        style={{
          border: `2px dashed ${callout.borderColor || '#94a3b8'}`,
          backgroundColor: callout.backgroundColor || (backgroundColor === 'transparent' ? '#f8fafc' : 'rgba(255, 255, 255, 0.1)')
        }}
      >
        {callout.title && (
          <div className="text-sm font-semibold mb-1" style={{ color: titleColor }}>
            {callout.title}
          </div>
        )}
        {callout.description && (
          <div className="text-sm" style={{ color: textColor }}>
            {callout.description}
          </div>
        )}
      </div>
    );
  };

  const renderMediaArea = () => {
    if (videoData?.src) {
      const aspectRatio = {
        '16-9': '16 / 9',
        '4-3': '4 / 3',
        '1-1': '1 / 1',
        '21-9': '21 / 9'
      }[videoData.aspectRatio || '16-9'] || '16 / 9';

      return (
        <div className="space-y-3">
          <div
            className="relative w-full overflow-hidden rounded-2xl bg-slate-900 shadow-lg"
            style={{ aspectRatio }}
          >
            <iframe
              src={videoData.src}
              className="w-full h-full border-0"
              allowFullScreen
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              referrerPolicy="strict-origin-when-cross-origin"
              title={videoData.videoTitle || `video-${columnIndex}-${cardIndex}`}
            />
          </div>
          {hasVideoCitation && (
            <div className="bg-gray-50 border border-gray-200 p-3 rounded-lg text-sm text-gray-600 text-left">
              {videoCitation}
              {videoUrl && (
                <a
                  href={videoUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-indigo-600 hover:text-indigo-800 hover:underline"
                >
                  {videoUrl}
                </a>
              )}
            </div>
          )}
          {isEditMode && (
            <button
              type="button"
              onClick={() => onRequestVideo?.(columnIndex, cardIndex, videoData)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-100 text-slate-800 text-sm font-semibold hover:bg-slate-200"
            >
              <Upload size={16} /> Replace video
            </button>
          )}
        </div>
      );
    }

    if (imageData?.src) {
      return (
        <div className="space-y-3 text-center">
          <img
            src={imageData.src}
            alt={imageData.alt || 'Slide image'}
            className="max-h-[320px] w-full object-contain rounded-xl shadow-lg"
          />
          {getDisplayCaption(imageData) && (
            <SafeHtml
              className="bg-gray-50 border border-gray-200 p-3 rounded-lg text-sm text-gray-700 text-left"
              html={getDisplayCaption(imageData)}
            />
          )}
          {imageCitation && (
            <div className="bg-gray-50 border border-gray-200 p-3 rounded-lg text-sm text-gray-600 text-left">
              <ApaSegments segments={imageCitation.segments} />
            </div>
          )}
          {isEditMode && (
            <button
              type="button"
              onClick={() => onRequestImage?.(columnIndex, cardIndex, imageData)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-100 text-slate-800 text-sm font-semibold hover:bg-slate-200"
            >
              <Upload size={16} /> Replace image
            </button>
          )}
        </div>
      );
    }

    if (showVideoPlaceholder) {
      return (
        <button
          type="button"
          onClick={() => onRequestVideo?.(columnIndex, cardIndex, videoData)}
          className="w-full h-full"
        >
          <div className="flex flex-col items-center justify-center gap-3 min-h-[260px] border-2 border-dashed border-slate-300 rounded-2xl bg-gradient-to-br from-slate-50 to-slate-100 p-6 hover:border-slate-400 hover:from-slate-100 hover:to-slate-50 transition-colors">
            <div className="w-12 h-12 rounded-full bg-white shadow flex items-center justify-center text-slate-500">
              <Video size={24} />
            </div>
            <div className="text-center">
              <p className="text-lg font-semibold text-slate-800">{card.placeholderHeading || 'Add Video'}</p>
              <p className="text-sm text-slate-600">{card.placeholderText || 'Insert a video directly into this slot.'}</p>
              <p className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-blue-600">
                <Upload size={14} /> Insert video
              </p>
            </div>
          </div>
        </button>
      );
    }

    if (showImagePlaceholder) {
      return (
        <button
          type="button"
          onClick={() => onRequestImage?.(columnIndex, cardIndex, imageData)}
          className="w-full h-full"
        >
          <div className="flex flex-col items-center justify-center gap-3 min-h-[260px] border-2 border-dashed border-slate-300 rounded-2xl bg-gradient-to-br from-slate-50 to-slate-100 p-6 hover:border-slate-400 hover:from-slate-100 hover:to-slate-50 transition-colors">
            <div className="w-12 h-12 rounded-full bg-white shadow flex items-center justify-center text-slate-500">
              <ImageIcon size={24} />
            </div>
            <div className="text-center">
              <p className="text-lg font-semibold text-slate-800">{card.placeholderHeading || 'Add Image'}</p>
              <p className="text-sm text-slate-600">{card.placeholderText || 'Insert an image directly into this slot.'}</p>
              <p className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-blue-600">
                <Upload size={14} /> Insert image
              </p>
            </div>
          </div>
        </button>
      );
    }

    return null;
  };

  const calloutPosition = callout.position || 'top';
  const cardNumber = (card?.number ?? cardIndex + 1).toString();
  const primaryTitleValue = card.primaryTitle ?? card.title ?? '';
  const handleActivate = (event) => {
    if (!isEditMode || isActive) return;
    if (event.target.closest('button, a, input, textarea, select, [contenteditable="true"], .ProseMirror')) return;
    onActivate?.();
  };

  return (
    <div
      className={`rounded-2xl p-6 shadow-sm transition-shadow hover:shadow-md ${isDragging ? 'opacity-60' : ''} ${fullHeight ? 'h-full flex flex-col' : ''}`}
      style={cardStyle}
      onClick={handleActivate}
    >
      {isEditMode && (
        <div className="flex justify-between items-center mb-3">
          <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/30 border border-white/40 text-base font-semibold" style={{ color: titleColor }}>
            {cardNumber}
          </span>
          <div className="flex items-center gap-2">
            {onDragStart && (
              <button
                type="button"
                className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-white/50 bg-white/70 text-slate-600 hover:text-slate-900 hover:bg-white transition"
                title="Drag to reorder"
                draggable
                onClick={(event) => event.stopPropagation()}
                onDragStart={onDragStart}
                onDragEnd={onDragEnd}
              >
                <GripVertical size={18} />
              </button>
            )}
            {isActive && (
              <button
                type="button"
                className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-white/50 bg-white/70 text-slate-600 hover:text-slate-900 hover:bg-white transition"
                title="Close edit mode"
                onClick={(event) => {
                  event.stopPropagation();
                  onDeactivate?.();
                }}
              >
                <X size={18} />
              </button>
            )}
          </div>
        </div>
      )}

      <div className={`space-y-2 ${isActive ? 'max-h-96 overflow-y-auto pr-1' : ''}`} style={headerStackAlignmentStyle}>
        {(categoryLabel || iconName || primaryTitleValue || isActive) && (
          <div
            className="flex items-start gap-3 w-full mb-1"
            style={headerAlignmentStyle}
          >
            {iconName && (
              <div
                className="w-10 h-10 rounded-2xl bg-white/20 border border-white/30 shadow-inner flex items-center justify-center"
                style={{ color: titleColor }}
              >
                {renderIcon(iconName)}
              </div>
            )}

            {(categoryLabel || primaryTitleValue || isActive) && (
              <div
                className="flex flex-col gap-1"
                style={{
                  textAlign: titleTextAlign,
                  alignItems:
                    titleAlignment === 'left'
                      ? 'flex-start'
                      : titleAlignment === 'right'
                        ? 'flex-end'
                        : 'center'
                }}
              >
                {isActive ? (
                  <>
                    <input
                      type="text"
                      value={categoryLabel || ''}
                      onChange={(event) => onCardUpdate?.({ categoryLabel: event.target.value })}
                      className="w-full rounded-md border border-gray-300 bg-white/90 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-700"
                      placeholder="Category label"
                    />
                    <input
                      type="text"
                      value={primaryTitleValue}
                      onChange={(event) => onCardUpdate?.({ primaryTitle: event.target.value, title: event.target.value })}
                      className="w-full rounded-md border border-gray-300 bg-white/90 px-3 py-2 text-base font-semibold text-slate-800"
                      placeholder="Card title"
                    />
                  </>
                ) : (
                  <>
                    {categoryLabel && (
                      <span
                        className="text-[11px] font-semibold tracking-wide uppercase"
                        style={{ color: titleColor }}
                      >
                        {categoryLabel}
                      </span>
                    )}

                    {primaryTitleValue && (
                      <h3
                        className="text-xl font-semibold"
                        style={{ color: titleColor, textAlign: titleTextAlign }}
                      >
                        {primaryTitleValue}
                      </h3>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {(instructionalText || isActive) && (
          isActive ? (
            <textarea
              value={instructionalText || ''}
              onChange={(event) => onCardUpdate?.({ instructionalText: event.target.value })}
              className="w-full rounded-md border border-gray-300 bg-white/90 px-3 py-2 text-sm text-slate-700"
              rows={3}
              placeholder="Instructional text"
            />
          ) : (
            instructionalText && (
              <p className="text-base mb-4" style={{ color: textColor, textAlign: titleTextAlign }}>
                {instructionalText}
              </p>
            )
          )
        )}
      </div>

      {/* Callout Box at Top */}
      {calloutPosition === 'top' && renderCalloutBox()}

      {renderMediaArea()}

      {numberedList.length > 0 && (
        <ol className="space-y-2 mb-4 list-none p-0">
          {numberedList.map((item, idx) => (
            <li key={idx} className="flex items-start gap-3">
              {useCheckmarks ? (
                <span
                  className="flex-shrink-0 w-8 h-8 rounded-full border-2 flex items-center justify-center text-base font-semibold"
                  style={{ borderColor: checkmarkColor, color: checkmarkColor, backgroundColor: 'rgba(255,255,255,0.08)' }}
                >
                  ✓
                </span>
              ) : (
                <span
                  className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-sm font-semibold"
                  style={badgeStyles}
                >
                  {idx + 1}
                </span>
              )}
              <span className="text-base" style={{ color: textColor }}>
                {item.text}
              </span>
            </li>
          ))}
        </ol>
      )}

      {card.isChecklist && card.checklistItems ? (
        <ul className="space-y-2">
          {card.checklistItems.map((item, idx) => (
            <li
              key={idx}
              className={`flex items-start gap-3 ${listStyle === 'numbered' && !item.checked ? 'opacity-80' : ''}`}
            >
              {listStyle === 'numbered' ? (
                <span
                  className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-sm font-semibold"
                  style={badgeStyles}
                >
                  {idx + 1}
                </span>
              ) : (
                <span className="text-xl mt-0.5" style={{ color: checkmarkColor }}>
                  {item.checked ? '✓' : '○'}
                </span>
              )}
              <div className="flex-1">
                <span
                  className={item.checked ? 'font-medium' : ''}
                  style={{ color: textColor }}
                >
                  {item.text}
                </span>
                {item.description && (
                  <span
                    className="block text-sm opacity-80"
                    style={{ color: textColor }}
                  >
                    {item.description}
                  </span>
                )}
              </div>
            </li>
          ))}
        </ul>
      ) : (
        isActive ? (
          <div className="max-h-[70vh] overflow-y-auto pr-1">
            <RichTextEditor
              content={card.content || ''}
              onChange={(content) => onCardUpdate?.({ content })}
              isPreviewMode={false}
            />
          </div>
        ) : (
          <SafeHtml
            className="card-content"
            style={{ color: textColor }}
            html={displayContent}
          />
        )
      )}

      {/* Callout Box at Bottom */}
      {calloutPosition === 'bottom' && renderCalloutBox()}
    </div>
  );
}

/**
 * Calculates optimal column widths based on content amount in each column
 * @param {Array} columns - Array of column objects with cards
 * @returns {Array} - Updated columns with optimized widthPercent values
 */
function calculateOptimalColumnWidths(columns) {
  if (!columns || columns.length === 0) return columns;

  // Calculate content weight for each column
  const columnWeights = columns.map(column => {
    const cards = column.cards || [];
    let totalWeight = 0;

    cards.forEach(card => {
      // Count content length
      const contentLength = (card.content || '').length;
      const titleLength = (card.title || card.primaryTitle || '').length;
      const checklistLength = (card.checklistItems || []).reduce((sum, item) => sum + (item.text || '').length, 0);
      const numberedListLength = (card.numberedList || []).reduce((sum, item) => sum + (item.text || '').length, 0);

      // Weight factors
      totalWeight += contentLength;
      totalWeight += titleLength * 2; // Titles have more visual weight
      totalWeight += checklistLength;
      totalWeight += numberedListLength;

      // Add weight for media
      if (card.image?.src || card.imagePlaceholder) totalWeight += 500;
      if (card.video?.src || card.videoPlaceholder) totalWeight += 600;

      // Number of cards also matters
      totalWeight += 100;
    });

    return Math.max(totalWeight, 100); // Minimum weight
  });

  // Calculate total weight
  const totalWeight = columnWeights.reduce((sum, weight) => sum + weight, 0);

  // Distribute percentages based on weights, but with bounds
  const MIN_WIDTH = 15; // Minimum 15% width
  const MAX_WIDTH = 70; // Maximum 70% width

  let adjustedWidths = columnWeights.map(weight => {
    const idealPercent = (weight / totalWeight) * 100;
    return Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, idealPercent));
  });

  // Normalize to ensure they sum to 100%
  const currentSum = adjustedWidths.reduce((sum, width) => sum + width, 0);
  adjustedWidths = adjustedWidths.map(width => (width / currentSum) * 100);

  // Apply to columns
  return columns.map((column, index) => ({
    ...column,
    widthPercent: Math.round(adjustedWidths[index] * 100) / 100
  }));
}

/**
 * MultiColumnCardRenderer - Renders cards in a flexible multi-column layout with two-layer approach
 * - Alignment layer: columns that should align across rows (using row-scoped grids)
 * - Stack layer: columns that stack independently (using flexbox)
 */
function MultiColumnCardRenderer({ block, isEditMode = false, onRequestImage, onRequestVideo, onBlockUpdate }) {
  const [activeCard, setActiveCard] = useState(null);
  const [autoConfigureEnabled, setAutoConfigureEnabled] = useState(block.autoConfigureEnabled ?? false);
  const [draggedCard, setDraggedCard] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);

  // Extract columns data early so it can be used in effects
  const columns = block.columns || block.multiColumnData?.columns || [];
  const columnCount = columns.length || 1;
  const getCardRowIndex = (card, fallbackIndex) => {
    const parsed = Number(card?.rowIndex);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : fallbackIndex + 1;
  };

  const columnCardEntries = useMemo(() => columns.map((column) => {
    const cards = column.cards || [];
    return cards
      .map((card, cardIndex) => ({
        card,
        cardIndex,
        rowIndex: getCardRowIndex(card, cardIndex)
      }))
      .sort((a, b) => {
        if (a.rowIndex !== b.rowIndex) return a.rowIndex - b.rowIndex;
        return a.cardIndex - b.cardIndex;
      });
  }), [columns]);

  useEffect(() => {
    if (!isEditMode) {
      setActiveCard(null);
      setDraggedCard(null);
      setDropTarget(null);
    }
  }, [isEditMode]);

  // Sync local state with block data when it changes
  useEffect(() => {
    setAutoConfigureEnabled(block.autoConfigureEnabled ?? false);
  }, [block.autoConfigureEnabled]);

  // Auto-apply optimized widths when auto-configure is enabled
  useEffect(() => {
    if (autoConfigureEnabled && columns.length > 0) {
      const optimizedColumns = calculateOptimalColumnWidths(columns);
      // Only update if widths have actually changed
      const widthsChanged = optimizedColumns.some((col, idx) =>
        col.widthPercent !== columns[idx]?.widthPercent
      );
      if (widthsChanged) {
        onBlockUpdate?.({
          ...block,
          columns: optimizedColumns,
          autoConfigureEnabled: true,
          multiColumnData: {
            ...(block.multiColumnData || {}),
            columns: optimizedColumns
          }
        });
      }
    }
  }, [autoConfigureEnabled, JSON.stringify(columns.map(col => ({
    cardCount: col.cards?.length,
    contentLengths: col.cards?.map(card => ({
      content: (card.content || '').length,
      title: (card.title || card.primaryTitle || '').length,
      checklist: (card.checklistItems || []).length,
      numbered: (card.numberedList || []).length,
      hasMedia: Boolean(card.image?.src || card.video?.src || card.imagePlaceholder || card.videoPlaceholder)
    }))
  })))]);

  const matchColumnHeights =
    typeof block.matchHeights === 'boolean'
      ? block.matchHeights
      : (block.multiColumnData?.matchHeights ?? false);
  const selectedMatchColumns = Array.isArray(block.matchHeightColumns || block.multiColumnData?.matchHeightColumns)
    ? (block.matchHeightColumns || block.multiColumnData?.matchHeightColumns)
    : [];

  const blockTitleEnabled =
    typeof block.blockTitleEnabled === 'boolean'
      ? block.blockTitleEnabled
      : Boolean(block.multiColumnData?.blockTitleEnabled);
  const blockTitle =
    (typeof block.blockTitle === 'string' && block.blockTitle.trim())
      ? block.blockTitle.trim()
      : (typeof block.multiColumnData?.blockTitle === 'string' ? block.multiColumnData.blockTitle.trim() : '');
  const showBlockTitle = Boolean(blockTitleEnabled && blockTitle);

  const selectedColumnSet = useMemo(
    () => new Set(
      selectedMatchColumns
        .map((value) => Number(value))
        .filter((value) => Number.isInteger(value) && value >= 0 && value < columnCount)
    ),
    [columnCount, JSON.stringify(selectedMatchColumns)]
  );

  const cardRefs = useRef({});
  const alignmentLayerRef = useRef(null);
  const stackLayerRef = useRef(null);
  const containerRef = useRef(null);

  const shouldMatchAllColumns = selectedColumnSet.size === 0;

  // Determine which column indices should be in alignment mode
  // Start with matchHeightColumns, expand to include columns touched by spanning cards
  const alignmentColumns = useMemo(() => {
    const baseColumns = matchColumnHeights
      ? shouldMatchAllColumns
        ? new Set(Array.from({ length: columnCount }, (_, i) => i))
        : new Set(selectedColumnSet)
      : new Set();

    // Expand to include columns touched by spanning cards
    columns.forEach((column, colIndex) => {
      const cards = column.cards || [];
      cards.forEach((card) => {
        const columnSpan = Math.min(
          Math.max(Number(card.columnSpan) || 1, 1),
          Math.max(1, columnCount - colIndex)
        );
        const spansMultipleColumns = columnSpan > 1;
        // When matching heights, inherit the selected alignment set.
        // Without height matching, only cards that span columns need alignment treatment.
        const touchesAlignment = matchColumnHeights
          ? Array.from({ length: columnSpan }).some((_, offset) => baseColumns.has(colIndex + offset))
          : spansMultipleColumns;

        if (touchesAlignment) {
          for (let i = colIndex; i < colIndex + columnSpan && i < columnCount; i++) {
            baseColumns.add(i);
          }
        }
      });
    });

    return baseColumns;
  }, [columnCount, columns, matchColumnHeights, shouldMatchAllColumns, selectedColumnSet]);

  // Build explicit row groups: treat each card index as a row
  const rowGroups = useMemo(() => {
    const rowMap = new Map();

    columnCardEntries.forEach((entries, colIndex) => {
      entries.forEach(({ card, cardIndex, rowIndex }) => {
        const columnSpan = Math.min(
          Math.max(Number(card.columnSpan) || 1, 1),
          Math.max(1, columnCount - colIndex)
        );

        if (!rowMap.has(rowIndex)) rowMap.set(rowIndex, []);
        rowMap.get(rowIndex).push({ card, colIndex, columnSpan, cardIndex, rowIndex });
      });
    });

    return Array.from(rowMap.entries())
      .sort(([a], [b]) => a - b)
      .map(([rowIndex, items]) => ({ rowIndex, items }));
  }, [columnCardEntries, columnCount]);

  // Height matching: measure and apply max height per row for alignment columns
  useLayoutEffect(() => {
    if (!matchColumnHeights) return;

    const cards = Object.values(cardRefs.current).filter(Boolean);
    let rafId = null;

    const measureHeights = () => {
      // Reset all heights first
      cards.forEach((el) => {
        el.style.height = '';
      });

      // Group cards by row index
      const rows = new Map();
      cards.forEach((el) => {
        const rowIndex = parseInt(el.dataset.rowIndex || '-1', 10);
        const colIndex = parseInt(el.dataset.colIndex || '0', 10);
        const span = parseInt(el.dataset.columnSpan || '1', 10);
        const inAlignment = el.dataset.inAlignment === 'true';

        if (!Number.isInteger(rowIndex) || rowIndex < 0 || !inAlignment) return;

        if (!rows.has(rowIndex)) rows.set(rowIndex, []);
        rows.get(rowIndex).push({ el, colIndex, span });
      });

      // For each row, find max height and apply to all cards in that row
      rows.forEach((rowCards) => {
        let maxHeight = 0;
        rowCards.forEach(({ el }) => {
          const rect = el.getBoundingClientRect();
          if (rect.height > maxHeight) maxHeight = rect.height;
        });

        if (maxHeight > 0) {
          rowCards.forEach(({ el }) => {
            el.style.height = `${maxHeight}px`;
          });
        }
      });
    };

    const observer = new ResizeObserver(() => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        measureHeights();
      });
    });

    cards.forEach((el) => {
      observer.observe(el);
    });

    measureHeights();

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      observer.disconnect();
    };
  }, [matchColumnHeights, alignmentColumns, columns]);

  // Set container min-height to prevent overlap between layers
  useLayoutEffect(() => {
    if (!containerRef.current) return;

    let rafId = null;

    const updateMinHeight = () => {
      if (!containerRef.current) return;

      const alignmentHeight = alignmentLayerRef.current?.offsetHeight || 0;
      const stackHeight = stackLayerRef.current?.offsetHeight || 0;
      const minHeight = Math.max(alignmentHeight, stackHeight);

      if (minHeight > 0) {
        containerRef.current.style.minHeight = `${minHeight}px`;
      } else if (alignmentHeight === 0 && stackHeight === 0) {
        // If both are 0, ensure we still have a minimal height
        containerRef.current.style.minHeight = '0px';
      }
    };

    const observer = new ResizeObserver(() => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        updateMinHeight();
      });
    });

    if (alignmentLayerRef.current) observer.observe(alignmentLayerRef.current);
    if (stackLayerRef.current) observer.observe(stackLayerRef.current);

    // Initial measurement with RAF to ensure DOM is ready
    rafId = requestAnimationFrame(() => {
      rafId = requestAnimationFrame(() => {
        updateMinHeight();
      });
    });

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      observer.disconnect();
    };
  }, [alignmentColumns, columns]);

  // Generate grid template columns based on custom widths or equal distribution
  const gridTemplateColumns = columns
    .map((col) => {
      const widthPercent = col.widthPercent || 100;
      return `${widthPercent / 100}fr`;
    })
    .join(' ');

  const setCardRef = (rowIndex, colIndex, span, key) => (el) => {
    if (el) {
      cardRefs.current[key] = el;
    } else {
      delete cardRefs.current[key];
    }
  };

  // Separate cards into alignment and stack groups
  const alignmentRowGroups = useMemo(() => {
    if (matchColumnHeights) {
      return rowGroups
        .map((group) => ({
          ...group,
          items: group.items.filter((item) => alignmentColumns.has(item.colIndex))
        }))
        .filter((group) => group.items.length > 0);
    }

    // When not matching heights, only render rows that contain spanning cards in the alignment layer
    return rowGroups
      .map((group) => ({
        ...group,
        items: group.items.filter((item) => item.columnSpan > 1)
      }))
      .filter((group) => group.items.length > 0);
  }, [alignmentColumns, matchColumnHeights, rowGroups]);

  const stackColumns = useMemo(() => {
    if (matchColumnHeights) {
      return columns
        .map((column, colIndex) => ({ column, colIndex }))
        .filter(({ colIndex }) => !alignmentColumns.has(colIndex));
    }

    // Without height matching, stack all columns so cards flow naturally
    return columns.map((column, colIndex) => ({ column, colIndex }));
  }, [alignmentColumns, columns, matchColumnHeights]);

  const handleCardUpdate = (colIndex, cardIndex, updates) => {
    const nextColumns = columns.map((column, columnIdx) => {
      if (columnIdx !== colIndex) return column;
      const nextCards = (column.cards || []).map((card, idx) => (
        idx === cardIndex ? { ...card, ...updates } : card
      ));
      return { ...column, cards: nextCards };
    });
    onBlockUpdate?.({
      ...block,
      columns: nextColumns,
      multiColumnData: {
        ...(block.multiColumnData || {}),
        columns: nextColumns
      }
    });
  };

  const handleMoveCard = (fromColIndex, fromCardIndex, targetColIndex, targetCardIndex) => {
    const preparedColumns = columns.map((col) => ({
      ...col,
      cards: [...(col.cards || [])]
    }));

    if (!preparedColumns[fromColIndex]?.cards?.length || !preparedColumns[targetColIndex]) {
      return;
    }

    const [cardToMove] = preparedColumns[fromColIndex].cards.splice(fromCardIndex, 1);
    if (!cardToMove) return;

    let safeTargetIndex = Math.min(
      Math.max(targetCardIndex, 0),
      preparedColumns[targetColIndex].cards.length
    );

    if (fromColIndex === targetColIndex && fromCardIndex < safeTargetIndex) {
      safeTargetIndex -= 1;
    }

    preparedColumns[targetColIndex].cards.splice(safeTargetIndex, 0, cardToMove);

    onBlockUpdate?.({
      ...block,
      columns: preparedColumns,
      multiColumnData: {
        ...(block.multiColumnData || {}),
        columns: preparedColumns
      }
    });
  };

  const getDropPosition = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const offset = event.clientY - rect.top;
    return offset < rect.height / 2 ? 'before' : 'after';
  };

  const handleCardDragStart = (colIndex, cardIndex) => (event) => {
    if (!isEditMode) return;
    event.stopPropagation();
    setDraggedCard({ colIndex, cardIndex });
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', 'card');
  };

  const handleCardDragEnd = () => {
    setDraggedCard(null);
    setDropTarget(null);
  };

  const handleCardDragOver = (colIndex, cardIndex) => (event) => {
    if (!isEditMode) return;
    event.preventDefault();
    const position = getDropPosition(event);
    setDropTarget({ colIndex, cardIndex, position });
  };

  const handleCardDrop = (colIndex, cardIndex) => (event) => {
    if (!isEditMode) return;
    event.preventDefault();
    event.stopPropagation();
    if (!draggedCard) return;

    const position = getDropPosition(event);
    const targetIndex = position === 'before' ? cardIndex : cardIndex + 1;

    if (draggedCard.colIndex === colIndex && draggedCard.cardIndex === targetIndex) {
      return;
    }

    handleMoveCard(draggedCard.colIndex, draggedCard.cardIndex, colIndex, targetIndex);
    setDraggedCard(null);
    setDropTarget(null);
  };

  const handleColumnDragOver = (colIndex) => (event) => {
    if (!isEditMode) return;
    event.preventDefault();
    setDropTarget({ colIndex, cardIndex: (columns[colIndex]?.cards || []).length, position: 'after', isColumn: true });
  };

  const handleColumnDrop = (colIndex, targetIndexOverride) => (event) => {
    if (!isEditMode) return;
    event.preventDefault();
    event.stopPropagation();
    if (!draggedCard) return;
    const targetIndex = Number.isInteger(targetIndexOverride)
      ? targetIndexOverride
      : (columns[colIndex]?.cards || []).length;
    handleMoveCard(
      draggedCard.colIndex,
      draggedCard.cardIndex,
      colIndex,
      targetIndex
    );
    setDraggedCard(null);
    setDropTarget(null);
  };

  const toggleActiveCard = (colIndex, cardIndex) => {
    setActiveCard((prev) => {
      if (prev && prev.colIndex === colIndex && prev.cardIndex === cardIndex) {
        return null;
      }
      return { colIndex, cardIndex };
    });
  };

  const handleAutoConfigureLayout = () => {
    const newState = !autoConfigureEnabled;
    setAutoConfigureEnabled(newState);

    if (newState) {
      // When turning ON, apply optimized widths immediately
      const optimizedColumns = calculateOptimalColumnWidths(columns);
      onBlockUpdate?.({
        ...block,
        columns: optimizedColumns,
        autoConfigureEnabled: true,
        multiColumnData: {
          ...(block.multiColumnData || {}),
          columns: optimizedColumns
        }
      });
    } else {
      // When turning OFF, just save the state (preserve current widths)
      onBlockUpdate?.({
        ...block,
        autoConfigureEnabled: false,
        multiColumnData: {
          ...(block.multiColumnData || {}),
        }
      });
    }
  };

  return (
    <div className="my-6">
      {showBlockTitle && (
        <div className="mb-6">
          <h3 className="text-2xl font-semibold text-blue-700 leading-tight">
            {blockTitle}
          </h3>
        </div>
      )}

      {isEditMode && (
        <div className="mb-4 flex gap-2">
          <button
            onClick={handleAutoConfigureLayout}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all shadow-sm font-medium text-sm ${
              autoConfigureEnabled
                ? 'bg-gradient-to-r from-purple-500 to-indigo-500 text-white hover:from-purple-600 hover:to-indigo-600'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-2 border-gray-300'
            }`}
            title={autoConfigureEnabled
              ? "Auto-configure is ON - column widths automatically adjust to content. Click to turn OFF and enable manual controls."
              : "Auto-configure is OFF - use manual controls to adjust column widths. Click to turn ON automatic optimization."
            }
          >
            <Wand2 size={16} />
            Auto-Configure: {autoConfigureEnabled ? 'ON' : 'OFF'}
          </button>
        </div>
      )}

      {/* Simple single-layer layout when not matching heights */}
      {!matchColumnHeights ? (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: columnCount > 1 ? gridTemplateColumns : '1fr',
            columnGap: '24px',
            alignItems: 'start'
          }}
        >
          {columns.map((column, colIndex) => {
            const cards = columnCardEntries[colIndex] || [];
            return (
              <div
                key={`col-${colIndex}`}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '24px',
                  minWidth: 0,  // Prevent grid blowout
                  overflow: 'hidden'  // Contain content within column
                }}
              >
                {cards.map(({ card, cardIndex }) => (
                  <div
                    key={`card-${colIndex}-${cardIndex}`}
                    onDragOver={isEditMode ? handleCardDragOver(colIndex, cardIndex) : undefined}
                    onDrop={isEditMode ? handleCardDrop(colIndex, cardIndex) : undefined}
                    style={{
                      minWidth: 0,  // Allow text to wrap
                      overflow: 'hidden'  // Prevent overflow
                    }}
                    className={
                      dropTarget?.colIndex === colIndex
                      && dropTarget?.cardIndex === cardIndex
                      && !dropTarget?.isColumn
                        ? dropTarget?.position === 'before'
                          ? 'border-t-4 border-emerald-400 rounded-2xl'
                          : 'border-b-4 border-emerald-400 rounded-2xl'
                        : ''
                    }
                  >
                    <CardItem
                      card={card}
                      columnIndex={colIndex}
                      cardIndex={cardIndex}
                      isEditMode={isEditMode}
                      isActive={Boolean(isEditMode && activeCard && activeCard.colIndex === colIndex && activeCard.cardIndex === cardIndex)}
                      onActivate={() => toggleActiveCard(colIndex, cardIndex)}
                      onDeactivate={() => setActiveCard(null)}
                      onCardUpdate={(updates) => handleCardUpdate(colIndex, cardIndex, updates)}
                      onDragStart={handleCardDragStart(colIndex, cardIndex)}
                      onDragEnd={handleCardDragEnd}
                      isDragging={Boolean(draggedCard && draggedCard.colIndex === colIndex && draggedCard.cardIndex === cardIndex)}
                      onRequestImage={onRequestImage}
                      onRequestVideo={onRequestVideo}
                      fullHeight={false}
                    />
                  </div>
                ))}
                {isEditMode && (
                  <div
                    onDragOver={handleColumnDragOver(colIndex)}
                    onDrop={handleColumnDrop(colIndex)}
                    className={`mt-2 rounded-xl border-2 border-dashed p-3 text-center text-xs font-semibold transition ${
                      dropTarget?.isColumn && dropTarget?.colIndex === colIndex
                        ? 'border-emerald-400 bg-emerald-50 text-emerald-700'
                        : draggedCard
                          ? 'border-emerald-200 text-emerald-600/60'
                          : 'border-transparent text-transparent'
                    }`}
                  >
                    Drop card to add to end
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        /* Two-layer layout for height matching */
        <div
          ref={containerRef}
          style={{
            position: 'relative',
            minHeight: '0px'
          }}
        >
          {/* Alignment Layer: Row-scoped grids for aligned columns */}
          <div
            ref={alignmentLayerRef}
            style={{
              position: 'relative',
              zIndex: 1
            }}
          >
            {alignmentRowGroups.map((group, groupIndex) => (
              <div
                key={`row-${group.rowIndex}`}
                style={{
                  display: 'grid',
                  gridTemplateColumns: columnCount > 1 ? gridTemplateColumns : '1fr',
                  columnGap: '24px',
                  marginBottom: groupIndex < alignmentRowGroups.length - 1 ? '24px' : '0',
                  alignItems: 'start'
                }}
              >
                {group.items.map((item) => {
                  const cardKey = `align-${item.colIndex}-${item.cardIndex}`;
                  return (
                    <div
                      key={cardKey}
                      ref={setCardRef(item.cardIndex, item.colIndex, item.columnSpan, cardKey)}
                      data-row-index={item.rowIndex}
                      data-col-index={item.colIndex}
                      data-column-span={item.columnSpan}
                      data-in-alignment="true"
                      onDragOver={isEditMode ? handleCardDragOver(item.colIndex, item.cardIndex) : undefined}
                      onDrop={isEditMode ? handleCardDrop(item.colIndex, item.cardIndex) : undefined}
                      style={{
                        gridColumn: `${item.colIndex + 1} / span ${item.columnSpan}`
                      }}
                      className={
                        dropTarget?.colIndex === item.colIndex
                        && dropTarget?.cardIndex === item.cardIndex
                        && !dropTarget?.isColumn
                          ? dropTarget?.position === 'before'
                            ? 'border-t-4 border-emerald-400 rounded-2xl'
                            : 'border-b-4 border-emerald-400 rounded-2xl'
                          : ''
                      }
                    >
                      <CardItem
                        card={item.card}
                        columnIndex={item.colIndex}
                        cardIndex={item.cardIndex}
                        isEditMode={isEditMode}
                        isActive={Boolean(isEditMode && activeCard && activeCard.colIndex === item.colIndex && activeCard.cardIndex === item.cardIndex)}
                        onActivate={() => toggleActiveCard(item.colIndex, item.cardIndex)}
                        onDeactivate={() => setActiveCard(null)}
                        onCardUpdate={(updates) => handleCardUpdate(item.colIndex, item.cardIndex, updates)}
                        onDragStart={handleCardDragStart(item.colIndex, item.cardIndex)}
                        onDragEnd={handleCardDragEnd}
                        isDragging={Boolean(draggedCard && draggedCard.colIndex === item.colIndex && draggedCard.cardIndex === item.cardIndex)}
                        onRequestImage={onRequestImage}
                        onRequestVideo={onRequestVideo}
                        fullHeight={matchColumnHeights}
                      />
                    </div>
                  );
                })}
              </div>
            ))}
            {isEditMode && (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: columnCount > 1 ? gridTemplateColumns : '1fr',
                  columnGap: '24px',
                  alignItems: 'start'
                }}
              >
                {columns.map((column, colIndex) => (
                  <div
                    key={`drop-end-${colIndex}`}
                    className={`rounded-xl border-2 border-dashed p-3 text-center text-xs font-semibold transition ${
                      dropTarget?.isColumn && dropTarget?.colIndex === colIndex
                        ? 'border-emerald-400 bg-emerald-50 text-emerald-700'
                        : draggedCard
                          ? 'border-emerald-200 text-emerald-600/60'
                          : 'border-transparent text-transparent'
                    }`}
                    onDragOver={handleColumnDragOver(colIndex)}
                    onDrop={handleColumnDrop(colIndex, (column.cards || []).length)}
                  >
                    Drop card to add to end
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Stack Layer: Independent vertical stacks for non-aligned columns */}
          {stackColumns.length > 0 && (
            <div
              ref={stackLayerRef}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                zIndex: 0,
                display: 'grid',
                gridTemplateColumns: columnCount > 1 ? gridTemplateColumns : '1fr',
                columnGap: '24px',
                pointerEvents: 'none'
              }}
            >
                {stackColumns.map(({ column, colIndex }) => {
                const cards = columnCardEntries[colIndex] || [];
                return (
                  <div
                    key={`stack-${colIndex}`}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '24px',
                      pointerEvents: 'auto',
                      gridColumn: colIndex + 1
                    }}
                    onDragOver={isEditMode ? handleColumnDragOver(colIndex) : undefined}
                    onDrop={isEditMode ? handleColumnDrop(colIndex) : undefined}
                  >
                    {cards.map(({ card, cardIndex, rowIndex }) => {
                      const columnSpan = Math.min(
                        Math.max(Number(card.columnSpan) || 1, 1),
                        Math.max(1, columnCount - colIndex)
                      );
                      if (!matchColumnHeights && columnSpan > 1) {
                        return null;
                      }
                      const cardKey = `stack-${colIndex}-${cardIndex}`;
                      return (
                        <div
                          key={cardKey}
                          ref={setCardRef(cardIndex, colIndex, columnSpan, cardKey)}
                          data-row-index={rowIndex}
                          data-col-index={colIndex}
                          data-column-span={columnSpan}
                          data-in-alignment="false"
                          onDragOver={isEditMode ? handleCardDragOver(colIndex, cardIndex) : undefined}
                          onDrop={isEditMode ? handleCardDrop(colIndex, cardIndex) : undefined}
                          className={
                            dropTarget?.colIndex === colIndex
                            && dropTarget?.cardIndex === cardIndex
                            && !dropTarget?.isColumn
                              ? dropTarget?.position === 'before'
                                ? 'border-t-4 border-emerald-400 rounded-2xl'
                                : 'border-b-4 border-emerald-400 rounded-2xl'
                              : ''
                          }
                        >
                          <CardItem
                            card={card}
                            columnIndex={colIndex}
                            cardIndex={cardIndex}
                            isEditMode={isEditMode}
                            isActive={Boolean(isEditMode && activeCard && activeCard.colIndex === colIndex && activeCard.cardIndex === cardIndex)}
                            onActivate={() => toggleActiveCard(colIndex, cardIndex)}
                            onDeactivate={() => setActiveCard(null)}
                            onCardUpdate={(updates) => handleCardUpdate(colIndex, cardIndex, updates)}
                            onDragStart={handleCardDragStart(colIndex, cardIndex)}
                            onDragEnd={handleCardDragEnd}
                            isDragging={Boolean(draggedCard && draggedCard.colIndex === colIndex && draggedCard.cardIndex === cardIndex)}
                            onRequestImage={onRequestImage}
                            onRequestVideo={onRequestVideo}
                            fullHeight={false}
                          />
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default MultiColumnCardRenderer;
